"use client";

import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "@/lib/utils";

type AnimateInProps = {
  children: ReactNode;
  /** Seconds. Keep it small (0–0.2); nothing should feel slow. */
  delay?: number;
  className?: string;
  as?: "div" | "section" | "article" | "li";
  id?: string;
};

/**
 * Quiet one-shot fade-up on enter (STYLE_GUIDE.md motion rules).
 * Content is server-rendered and stays in the DOM; only opacity/translate
 * change, so SEO is unaffected. Respects prefers-reduced-motion.
 */
export function AnimateIn({
  children,
  delay = 0,
  className,
  as: Tag = "div",
  id,
}: AnimateInProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      el.classList.add("visible");
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add("visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -32px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      id={id}
      className={cn("animate-in-view", className)}
      style={delay ? { transitionDelay: `${delay}s` } : undefined}
    >
      {children}
    </Tag>
  );
}

AnimateIn.displayName = "AnimateIn";
